import React, { useEffect, useState } from 'react';
import {
  Field,
  Dropdown,
  Option,
  Button,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { Info20Regular } from '@fluentui/react-icons';
import BaseDialog from '../BaseDialog';

/**
 * Order-level coupon dialog. Lets staff pick a predefined coupon to apply to
 * the order from a dropdown.
 *
 * STATUS: stubbed UI — a planned feature. No coupons are defined yet, so the
 * dropdown renders empty and the "Apply" action is intentionally disabled.
 * When wired up, coupons will be managed as their own records and applied to
 * the Booqable order via its coupon mechanism (order.coupon / coupon_percent_off
 * are Booqable-native), with the catalog of coupons sourced from the backend.
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// Placeholder for the eventual coupon catalog. Empty by design — coupons don't
// exist yet; this is what the dropdown will map over once they're defined.
interface CouponOption {
  id: string;
  label: string;
}
const DEFINED_COUPONS: CouponOption[] = [];

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  // Neutral (not warning-styled) info bar — communicates "not built yet", which
  // is informational rather than a warning state.
  plannedNote: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('10px', '12px'),
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius('4px'),
    color: tokens.colorNeutralForeground2,
    fontSize: '13px',
  },
});

export const CouponDialog: React.FC<Props> = ({ isOpen, onClose }) => {
  const styles = useStyles();
  const [couponId, setCouponId] = useState<string>('');

  useEffect(() => {
    if (!isOpen) return;
    setCouponId('');
  }, [isOpen]);

  const selected = DEFINED_COUPONS.find((c) => c.id === couponId);

  return (
    <BaseDialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add a coupon"
      maxWidth="460px"
      actions={
        <>
          <Button appearance="secondary" onClick={onClose}>
            Cancel
          </Button>
          {/* Disabled until coupons exist and the Booqable wiring lands. */}
          <Button appearance="primary" disabled title="Coupons aren't available yet">
            Apply
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <div className={styles.plannedNote}>
          <Info20Regular />
          <span>Coupons aren't available yet — this is a planned feature.</span>
        </div>

        <Field
          label="Coupon"
          hint={DEFINED_COUPONS.length === 0 ? 'No coupons defined yet.' : undefined}
        >
          <Dropdown
            size="large"
            placeholder="Select a coupon"
            value={selected?.label ?? ''}
            selectedOptions={couponId ? [couponId] : []}
            onOptionSelect={(_, d) => setCouponId(d.optionValue || '')}
            disabled={DEFINED_COUPONS.length === 0}
          >
            {DEFINED_COUPONS.map((c) => (
              <Option key={c.id} value={c.id} text={c.label}>
                {c.label}
              </Option>
            ))}
          </Dropdown>
        </Field>
      </div>
    </BaseDialog>
  );
};

export default CouponDialog;
